import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Calendar, Mail, Linkedin, ChevronDown, ChevronUp, Target, Wrench, MessageCircle, Users, Clock, FileText, Shield, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { QuoteRequestModal } from '../components/forms/QuoteRequestModal';
import { SEO } from '../components/SEO';

export default function ConsultingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState('');

  const engagements = [
    {
      icon: Target,
      title: 'Strategic Advisory',
      duration: '4-12 weeks',
      price: 'Custom Pricing',
      description: 'Current state assessment, technology landscape analysis, and strategic roadmaps.',
      included: [
        'Current state assessment',
        'Technology landscape analysis',
        'Multi-option architecture recommendations',
        'Cost-benefit analysis with ROI projections',
        'Vendor evaluation and selection support',
        '90-day implementation roadmap',
        'Executive presentation of findings',
      ],
      deliverables: [
        'Strategic architecture document (40-60 pages)',
        'Cost model and ROI analysis',
        'Implementation roadmap',
        'Risk assessment and mitigation plan',
      ],
      idealFor: [
        'Planning cloud migrations/modernization',
        'Evaluating multi-model LLM strategies',
        'Build-vs-buy decisions',
        'Preparing budget proposals',
      ],
    },
    {
      icon: Wrench,
      title: 'Implementation Partnership',
      duration: '3-6 months',
      price: 'Custom Pricing',
      description: 'Hands-on architecture and deployment, embedded with your engineering team.',
      included: [
        'Hands-on architecture and deployment',
        'Embedded with engineering team',
        'Weekly architecture reviews',
        'Code/IaC review and optimization',
        'DevOps pipeline and SRE practices setup',
        'Team training and mentorship',
        'Documentation and runbooks',
      ],
      deliverables: [
        'Production-ready infrastructure',
        'Operational documentation',
        'Team training materials',
        'Automated deployment pipelines',
      ],
      idealFor: [
        'Deploying multi-model LLM platforms',
        'Building Kubernetes-based infrastructure',
        'Implementing DevSecOps and SRE for AI/ML systems',
        'Migrating to cloud/multi-cloud',
      ],
    },
    {
      icon: MessageCircle,
      title: 'Ongoing Advisory',
      duration: '6-12 month retainers',
      price: 'Custom Pricing',
      description: 'Continuous strategic guidance with monthly architecture reviews.',
      included: [
        'Monthly architecture review sessions (2-4 hours)',
        'Async Slack/email access',
        'Quarterly technology landscape updates',
        'Vendor roadmap analysis',
        'Team mentorship',
        'Ad-hoc presentations to leadership',
        'Early access to research',
      ],
      deliverables: [
        'Monthly architecture review notes',
        'Quarterly landscape reports',
        'Vendor evaluation memos',
        'Continuous strategic guidance',
      ],
      idealFor: [
        'Organizations lacking deep infrastructure expertise',
        'Teams navigating rapid tech change',
        'CIOs/CTOs needing trusted advisor',
        'Companies making ongoing build-vs-buy decisions',
      ],
    },
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery Call',
      duration: '30 minutes, free',
      description: 'Discuss challenges, goals, constraints, timeline, budget, fit.',
    },
    {
      step: '02',
      title: 'Scope Definition',
      duration: '1 week',
      description: 'Provide statement of work, timeline, milestones, investment, success criteria.',
    },
    {
      step: '03',
      title: 'Kickoff',
      duration: 'Week 1',
      description: 'Establish communication, cadence, documentation, alignment.',
    },
    {
      step: '04',
      title: 'Execution',
      duration: 'Variable',
      description: 'Weekly check-ins, deliverables, collaboration, updates.',
    },
    {
      step: '05',
      title: 'Closeout & Transition',
      duration: 'Final phase',
      description: 'Final deliverables, knowledge transfer, support recommendations.',
    },
  ];

  const faqs = [
    {
      q: 'What industries do you work with?',
      a: 'All industries, with deep experience in financial services, healthcare, technology, and professional services.',
    },
    {
      q: 'Do you work remotely or on-site?',
      a: 'Both. Remote for advisory engagements, typically monthly on-site for implementation. Flexible based on needs.',
    },
    {
      q: 'How quickly can you start?',
      a: 'Strategic advisory: 1-2 weeks. Implementation: 2-4 weeks. Retainers: Immediate (limited availability).',
    },
    {
      q: 'Do you sign NDAs?',
      a: 'Yes, mutual NDAs are standard for all engagements.',
    },
    {
      q: "What if we're not satisfied?",
      a: "Milestone-based payments with clear success criteria. I'll work to make it right or part ways professionally.",
    },
    {
      q: 'Can you provide references?',
      a: 'Yes, I can connect you with past clients (with their permission) upon request.',
    },
  ];

  const handleQuoteRequest = (engagementTitle) => {
    setSelectedEngagement(engagementTitle);
    setIsQuoteModalOpen(true);
  };

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Siddharth Kaul Consulting",
    "description": "Enterprise cloud infrastructure and AI consulting services for Fortune 500 companies",
    "url": "https://siddharthkaul.com/consulting",
    "areaServed": "Worldwide",
    "serviceType": [
      "Cloud Infrastructure Consulting",
      "AI/ML Platform Architecture",
      "DevSecOps Implementation",
      "Multi-cloud Strategy",
      "Kubernetes Consulting"
    ],
    "provider": {
      "@type": "Person",
      "name": "Siddharth Kaul",
      "jobTitle": "Associate Director of Cloud & AI Infrastructure"
    },
    "priceRange": "Contact for pricing",
    "offers": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Strategic Advisory",
          "description": "4-12 week cloud infrastructure strategic planning and roadmap development"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Implementation Partnership",
          "description": "3-6 month hands-on infrastructure implementation and deployment"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Ongoing Advisory",
          "description": "6-12 month retainer-based consulting and strategic guidance"
        }
      }
    ]
  };

  return (
    <main className="pt-20" data-testid="consulting-page">
      <SEO
        title="Cloud & AI Infrastructure Consulting Services"
        description="Enterprise cloud infrastructure consulting: strategic advisory, implementation support, and ongoing advisory for multi-cloud, AI/ML, and DevSecOps transformations. Custom pricing based on your needs."
        keywords="cloud consulting services, AI infrastructure consulting, DevSecOps consulting, multi-cloud strategy, Kubernetes consulting, cloud migration services, enterprise cloud architecture"
        canonicalUrl="/consulting"
        schema={professionalServiceSchema}
      />
      <QuoteRequestModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        engagementType={selectedEngagement}
      />
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Consulting Services
            </p>
            <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-6">
              Work With Me
            </h1>
            <p className="text-lg text-[#64748B] leading-relaxed mb-8">
              I help enterprise infrastructure leaders make better technology decisions and execute transformations that deliver measurable ROI.
            </p>

            {/* Key Results */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
              {[
                { value: '40-70%', label: 'Typical Cost Reduction' },
                { value: '90 Days', label: 'Time to Production' },
                { value: 'Strategic', label: 'Roadmaps Aligned' },
                { value: 'Teams', label: 'Equipped to Own' },
              ].map((item, idx) => (
                <div key={idx} className="text-center p-4 bg-[#F8F9FA] rounded-lg">
                  <p className="font-['Montserrat'] font-bold text-2xl text-[#3498DB]">{item.value}</p>
                  <p className="text-sm text-[#64748B]">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#64748B] italic">
              * Results based on past client engagements. Actual outcomes vary by project scope and implementation.
            </p>
          </div>
        </div>
      </section>

      {/* Engagement Types */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50] mb-4">
              Engagement Types
            </h2>
            <p className="text-[#64748B]">
              Choose the engagement model that best fits your needs and timeline.
            </p>
          </div>

          <div className="space-y-8">
            {engagements.map((eng, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden card-hover"
                data-testid={`engagement-card-${index}`}
              >
                <div className="p-8">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center">
                        <eng.icon className="text-[#3498DB]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50]">
                          {eng.title}
                        </h3>
                        <p className="text-[#64748B] text-sm">{eng.duration}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#64748B]">Investment</p>
                      <p className="font-['Montserrat'] font-bold text-xl text-[#2C3E50]">
                        {eng.price}
                      </p>
                    </div>
                  </div>

                  <p className="text-[#64748B] mb-6">{eng.description}</p>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                      <h4 className="font-semibold text-[#2C3E50] mb-3 flex items-center gap-2">
                        <CheckCircle size={16} className="text-[#27AE60]" />
                        What's Included
                      </h4>
                      <ul className="space-y-2">
                        {eng.included.map((item, idx) => (
                          <li key={idx} className="text-sm text-[#64748B] flex items-start gap-2">
                            <span className="text-[#3498DB] mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#2C3E50] mb-3 flex items-center gap-2">
                        <FileText size={16} className="text-[#3498DB]" />
                        Deliverables
                      </h4>
                      <ul className="space-y-2">
                        {eng.deliverables.map((item, idx) => (
                          <li key={idx} className="text-sm text-[#64748B] flex items-start gap-2">
                            <span className="text-[#3498DB] mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#2C3E50] mb-3 flex items-center gap-2">
                        <Users size={16} className="text-[#3498DB]" />
                        Ideal For
                      </h4>
                      <ul className="space-y-2">
                        {eng.idealFor.map((item, idx) => (
                          <li key={idx} className="text-sm text-[#64748B] flex items-start gap-2">
                            <span className="text-[#3498DB] mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <Button
                      onClick={() => handleQuoteRequest(eng.title)}
                      className="w-full bg-[#3498DB] hover:bg-[#2980B9] text-white shadow-[0_4px_14px_0_rgba(52,152,219,0.39)]"
                      data-testid={`quote-button-${index}`}
                    >
                      <DollarSign size={18} className="mr-2" />
                      Request Custom Quote
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Process
            </p>
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50]">
              How We'll Work Together
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="relative p-6 bg-[#F8F9FA] rounded-xl"
                data-testid={`process-step-${index}`}
              >
                <span className="text-[#3498DB] font-['Montserrat'] font-bold text-3xl">
                  {step.step}
                </span>
                <h4 className="font-['Montserrat'] font-semibold text-[#2C3E50] mt-2 mb-1">
                  {step.title}
                </h4>
                <p className="text-xs text-[#3498DB] mb-2">{step.duration}</p>
                <p className="text-sm text-[#64748B]">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2">
                    <ArrowRight size={16} className="text-[#CBD5E1]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Placeholder */}
      <section className="py-16 md:py-24 bg-[#2C3E50]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Testimonials
            </p>
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-white mb-8">
              What Clients Say
            </h2>
            <div className="bg-white/10 rounded-xl p-8">
              <p className="text-slate-300 italic mb-4">
                "Client testimonials coming soon. Contact me for references from past engagements."
              </p>
              <p className="text-slate-400 text-sm">- Fortune 500 Client References Available on Request</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              FAQs
            </p>
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50]">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4" data-testid="faq-section">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-slate-200 rounded-lg overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F8F9FA] transition-colors duration-200"
                >
                  <span className="font-medium text-[#2C3E50]">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp size={20} className="text-[#64748B]" />
                  ) : (
                    <ChevronDown size={20} className="text-[#64748B]" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="p-4 pt-0 text-[#64748B]">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-['Montserrat'] font-bold text-3xl md:text-4xl text-[#2C3E50] mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-[#64748B] mb-8">
              Book a free 30-minute discovery call to discuss your challenges and goals.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                asChild
                className="bg-[#3498DB] hover:bg-[#2980B9] text-white px-8 py-6 text-base shadow-[0_4px_14px_0_rgba(52,152,219,0.39)]"
              >
                <Link to="/contact">
                  <Calendar size={18} className="mr-2" />
                  Book Discovery Call
                </Link>
              </Button>
              <Button
                onClick={() => handleQuoteRequest('')}
                variant="outline"
                className="border-2 border-[#3498DB] text-[#3498DB] hover:bg-[#3498DB] hover:text-white px-8 py-6 text-base"
              >
                <DollarSign size={18} className="mr-2" />
                Request Quote
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-[#2C3E50] text-[#2C3E50] hover:bg-[#2C3E50] hover:text-white px-8 py-6 text-base"
              >
                <a href="mailto:contact@siddharthkaul.com">
                  <Mail size={18} className="mr-2" />
                  Email Directly
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
